import React from 'react';

import { Box } from '@rbilabs/components-library';
import Carousel from 'nuka-carousel';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ActionLink } from 'components/action-button';
import MenuCardButton from 'components/menu-card-button';
import MenuSectionHeading from 'components/menu-section-heading';
import Picture from 'components/picture';
import BlockRenderer from 'components/sanity-block-renderer';
import { IFanFavoritesFragment } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';

const Section = styled.section`
  position: relative;
  padding: 1.5rem 0;

  ${Styles.desktop`
    padding: 0;
  `}
`;

const FavoritesCardContainer = styled.div`
  display: flex;
  margin: 0 auto;
  padding: 0 3rem;
  max-width: 75rem;

  /* nuka-carousel overrides */
  .slider-frame {
    border-radius: ${Styles.borderRadius};
  }

  .slider-slide {
    cursor: default;
  }
`;

const FavoriteCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 26rem;
  background: ${Styles.color.white};

  &:focus {
    outline-offset: -3px;
  }

  ${Styles.desktop`
    height: 30rem;
  `}
`;

const FavoriteCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 0 50%;
  padding: 0 1.5rem 0 1.5rem;
  text-align: center;
`;

const FavoriteCardContentHeading = styled.div`
  font-size: 1.375rem;
  font-family: ${Styles.fontFamily.base};
  font-weight: ${Styles.fontWeight.heavy};
  line-height: 1;
  margin: 0 0 0.5rem 0;
  text-transform: ${Styles.textTransform.headlines};
`;

const FavoriteCardContentTagline = styled(BlockRenderer)`
  color: ${Styles.color.grey.two};
  font-family: ${Styles.fontFamily.body};
  width: 75%;

  ${Styles.desktop`
    width: 100%;
  `}
`;

const FavoriteItemImageContent = styled.div`
  flex: 0 0 50%;
  overflow: hidden;
  width: 100%;
`;

const FavoriteCarouselBackward = styled(MenuCardButton)`
  transform: rotate(180deg) translateX(50%);

  &:active {
    transform: rotate(180deg) translateX(50%) scale(0.95);
  }
`;

const FavoriteCarouselForward = styled(MenuCardButton)`
  transform: translateX(50%);

  &:active {
    transform: translateX(50%) scale(0.95);
  }
`;

const FanFavoritesSection: React.FC<{
  item: IFanFavoritesFragment;
}> = ({ item }) => {
  const isDesktop = useMediaQuery('desktop');
  const isDesktopLarge = useMediaQuery('desktopLarge');

  const { favoriteSection, fanFavoriteItems } = item || {};
  const heading = favoriteSection?.headerText?.locale || 'Fan Favorites';
  const { getLocalizedRouteForPath } = useRoutes();
  // Defaults to English. Should be moved to localized later when supported
  const exploreMenuRoute = getLocalizedRouteForPath('explore-menu');
  const { formatMessage } = useIntl();
  const favorites = fanFavoriteItems ?? [];
  const favoriteCards = [
    // Duplicating cards so scrolling is smooth when
    // we only have 4 items
    ...favorites,
    ...favorites,
  ].reduce((arr: JSX.Element[], favorite, index) => {
    if (favorite && '_id' in favorite && favorite.image) {
      arr.push(
        <FavoriteCard key={favorite._id}>
          {index % 2 === 0 && (
            <FavoriteItemImageContent>
              <Picture image={favorite.image} alt="" />
            </FavoriteItemImageContent>
          )}
          <FavoriteCardContent>
            <FavoriteCardContentHeading>{favorite.name?.locale}</FavoriteCardContentHeading>
            <FavoriteCardContentTagline content={favorite?.description?.localeRaw} />
          </FavoriteCardContent>
          {index % 2 === 1 && (
            <FavoriteItemImageContent>
              <Picture image={favorite.image} alt="" />
            </FavoriteItemImageContent>
          )}
        </FavoriteCard>
      );
    }
    return arr;
  }, []);

  if (!item) {
    return null;
  }

  return (
    <Section>
      <MenuSectionHeading as="h2">{heading}</MenuSectionHeading>
      <FavoritesCardContainer>
        <Carousel
          slidesToShow={isDesktopLarge ? 4 : isDesktop ? 3 : 1}
          renderBottomCenterControls={null}
          renderCenterLeftControls={({ previousSlide }) => (
            <FavoriteCarouselBackward
              onClick={previousSlide}
              aria-label={formatMessage({ id: 'backwardButton' })}
            />
          )}
          renderCenterRightControls={({ nextSlide }) => (
            <FavoriteCarouselForward
              onClick={nextSlide}
              aria-label={formatMessage({ id: 'forwardButton' })}
            />
          )}
          wrapAround={true}
        >
          {favoriteCards}
        </Carousel>
      </FavoritesCardContainer>
      <Box margin="2rem auto 1rem" width="100%" justify="center">
        <ActionLink to={exploreMenuRoute}>{formatMessage({ id: 'exploreOurMenu' })}</ActionLink>
      </Box>
    </Section>
  );
};

export default FanFavoritesSection;
